import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { apiFetch } from '@shared/api/http'
import { verificationKeys } from './verification'

const EnrollmentSchema = z.object({
  status: z.enum(['not_started', 'creation_pending', 'creation_failed', 'account_linked']),
  hasTermsAcceptance: z.boolean(),
  creationAvailable: z.boolean(),
  retryAvailable: z.boolean().default(true),
  eligibility: z.discriminatedUnion('allowed', [
    z.object({ allowed: z.literal(true) }),
    z.object({
      allowed: z.literal(false),
      reason: z.enum([
        'missing_identity',
        'invalid_identity',
        'missing_applicable_consent',
        'configuration_unavailable',
      ]),
    }),
  ]),
  identity: z.object({
    businessType: z.enum(['INDIVIDUAL', 'COMPANY']).optional(),
    documentType: z.enum(['CPF', 'CNPJ']).optional(),
    documentNumber: z.string().optional(),
  }),
  accountId: z.string().optional(),
  failureCode: z.string().optional(),
  message: z.string().optional(),
})

export type Enrollment = z.infer<typeof EnrollmentSchema>
const key = ['kyc', 'enrollment'] as const
export const enrollmentKeys = { view: key } as const
export const enrollmentMutationKey = ['kyc', 'enrollment', 'create'] as const

const CREATION_PENDING_POLL_MS = 3_000
const CREATION_PENDING_POLL_LIMIT = 20

export function useEnrollment() {
  return useQuery({
    queryKey: key,
    queryFn: async () => EnrollmentSchema.parse(await apiFetch<unknown>('/kyc/enrollment')),
    refetchInterval: (query) =>
      query.state.data?.status === 'creation_pending' &&
      query.state.dataUpdateCount < CREATION_PENDING_POLL_LIMIT
        ? CREATION_PENDING_POLL_MS
        : false,
  })
}

export function useCreateEnrollment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: enrollmentMutationKey,
    mutationFn: async () =>
      EnrollmentSchema.parse(
        await apiFetch<unknown>('/kyc/enrollment', {
          method: 'POST',
          body: {},
        }),
      ),
    onSuccess: (value) => {
      queryClient.setQueryData(key, value)
      if (value.status === 'account_linked') {
        void queryClient.invalidateQueries({ queryKey: enrollmentKeys.view })
        void queryClient.invalidateQueries({ queryKey: verificationKeys.view })
      }
    },
  })
}
